import { useEffect, useRef, useState } from "react";
import { FiCheck, FiCopy, FiDownload, FiEdit2, FiEye, FiLink, FiMail, FiRefreshCw, FiRotateCcw, FiSlash, FiTrash2, FiX } from "react-icons/fi";
import QRCode from "qrcode";
import type { AdminEventSummary, AdminGuestRow, AdminListResponse, CapValue, RsvpCaps } from "@firsttoast/shared";
import { UNLIMITED_HARD_MAX } from "@firsttoast/shared";
import {
  adminCreateGuest,
  adminCreateEvent,
  adminDeleteGuest,
  adminEvents,
  adminList,
  adminLogin,
  adminSendInvite,
  adminUpdateEvent,
  adminUpdateGuest,
} from "../api.js";

const SESSION_KEY = "ft_admin_session";
const DEFAULT_NEW_CAPS: RsvpCaps = { adults: "unlimited", kids: "unlimited" };


function CapInput({ value, onChange }: { value: CapValue; onChange: (c: CapValue) => void }) {
  const unlimited = value === "unlimited";
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        max={UNLIMITED_HARD_MAX}
        disabled={unlimited}
        className="field w-16 disabled:opacity-40"
        value={unlimited ? "" : value}
        onChange={(e) =>
          onChange(Math.max(0, Math.min(UNLIMITED_HARD_MAX, Number(e.target.value))))
        }
      />
      <label className="flex items-center gap-1 text-sm text-muted">
        <input
          type="checkbox"
          className="accent-accent"
          checked={unlimited}
          onChange={(e) => onChange(e.target.checked ? "unlimited" : 2)}
        />
        ∞
      </label>
    </div>
  );
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10)
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1")
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return raw; // international or non-standard — show as-is
}

function PhoneCell({ phone }: { phone: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(phone);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <span className="flex items-center gap-1.5">
      <span>{formatPhone(phone)}</span>
      <button type="button" onClick={copy} title="Copy number" className="text-muted hover:text-ink transition">
        {copied ? <FiCheck size={12} className="text-green-600" /> : <FiCopy size={12} />}
      </button>
    </span>
  );
}

function StatusBadge({ guest }: { guest: AdminGuestRow }) {
  if (guest.revoked)
    return <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Revoked</span>;
  if (guest.invitation?.error && !guest.invitation.sentAt)
    return <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">Failed</span>;
  if (!guest.invitation?.sentAt)
    return <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-500">Pending</span>;
  if (guest.rsvp?.status === "yes")
    return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Attending</span>;
  if (guest.rsvp?.status === "no")
    return <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-600">Declined</span>;
  if (guest.viewedAt)
    return <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">Viewed</span>;
  return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">Invited</span>;
}

// Shared modal for adding a new guest or editing an existing one.
function GuestModal({
  mode,
  guest,
  eventId,
  session,
  onDone,
  onClose,
}: {
  mode: "add" | "edit";
  guest?: AdminGuestRow;
  eventId: string;
  session: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(guest?.name ?? "");
  const [email, setEmail] = useState(guest?.email ?? "");
  const [phone, setPhone] = useState(guest?.phone ?? "");
  const [tableNumber, setTableNumber] = useState(guest?.tableNumber ?? "");
  const [isTest, setIsTest] = useState(!!guest?.isTest);
  const [caps, setCaps] = useState<RsvpCaps>(guest?.caps ?? DEFAULT_NEW_CAPS);
  // RSVP override (edit mode only)
  const [rsvpStatus, setRsvpStatus] = useState<"yes" | "no" | "">(guest?.rsvp?.status ?? "");
  const [rsvpAdults, setRsvpAdults] = useState(guest?.rsvp?.adults ?? 1);
  const [rsvpKids, setRsvpKids] = useState(guest?.rsvp?.kids ?? 0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === "add") {
        await adminCreateGuest(session, {
          eventId,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          isTest,
          caps,
        });
      } else {
        const rsvpPayload = rsvpStatus === ""
          ? undefined
          : rsvpStatus === "yes"
            ? { status: "yes" as const, adults: rsvpAdults, kids: rsvpKids }
            : { status: "no" as const, adults: 0, kids: 0 };
        await adminUpdateGuest(session, guest!.token, {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          tableNumber: tableNumber.trim(),
          isTest,
          caps,
          rsvp: rsvpPayload,
        });
      }
      onDone();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card w-full max-w-lg">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{mode === "add" ? "Add guest" : "Edit guest"}</h2>
          <button type="button" className="text-muted hover:text-ink transition" onClick={onClose}><FiX size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-muted">Full name</span>
            <input required className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-muted">Email</span>
            <input required type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-muted">Phone (optional)</span>
            <input type="tel" className="field" placeholder="+1…" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-muted">Table # (optional)</span>
            <input className="field" placeholder="e.g. 4" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" className="accent-accent" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
            Test guest
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted">Adults</span>
            <CapInput value={caps.adults} onChange={(adults) => setCaps({ ...caps, adults })} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted">Kids</span>
            <CapInput value={caps.kids} onChange={(kids) => setCaps({ ...caps, kids })} />
          </div>
          {mode === "edit" && (
            <div className="sm:col-span-2 border-t border-accent/10 pt-4">
              <p className="mb-3 text-sm font-semibold text-muted uppercase tracking-wide">RSVP override</p>
              <div className="flex gap-2 mb-3">
                {(["", "yes", "no"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRsvpStatus(v)}
                    className={`rounded-full px-3 py-1 text-sm border transition ${
                      rsvpStatus === v
                        ? v === "yes" ? "bg-green-600 text-white border-green-600"
                          : v === "no" ? "bg-neutral-500 text-white border-neutral-500"
                          : "bg-accent text-white border-accent"
                        : "border-black/10 text-muted hover:bg-black/5"
                    }`}
                  >
                    {v === "" ? "No override" : v === "yes" ? "Attending" : "Declined"}
                  </button>
                ))}
              </div>
              {rsvpStatus === "yes" && (
                <div className="flex gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-muted">Adults</span>
                    <input type="number" min={0} max={30} className="field w-20" value={rsvpAdults} onFocus={(e) => e.target.select()} onChange={(e) => setRsvpAdults(Math.max(0, Number(e.target.value)))} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-sm text-muted">Kids</span>
                    <input type="number" min={0} max={30} className="field w-20" value={rsvpKids} onFocus={(e) => e.target.select()} onChange={(e) => setRsvpKids(Math.max(0, Number(e.target.value)))} />
                  </label>
                </div>
              )}
            </div>
          )}
          {err && <p className="text-red-700 sm:col-span-2">{err}</p>}
          <div className="flex gap-3 sm:col-span-2">
            <button className="btn-primary" disabled={busy || !name.trim() || !email.trim()}>
              {busy ? "Saving…" : mode === "add" ? "Add guest" : "Save changes"}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GuestRow({
  guest,
  session,
  origin,
  onChanged,
  onEdit,
  checked,
  onToggle,
}: {
  guest: AdminGuestRow;
  session: string;
  origin: string;
  onChanged: () => void;
  onEdit: (guest: AdminGuestRow) => void;
  checked: boolean;
  onToggle: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const isSent = !!guest.invitation?.sentAt;
  const rsvpAdults = guest.rsvp?.status === "yes" ? guest.rsvp.adults : 0;
  const rsvpKids = guest.rsvp?.status === "yes" ? guest.rsvp.kids : 0;

  async function send() {
    setBusy(true); setRowError(null);
    try { await adminSendInvite(session, guest.token); }
    catch (err) { setRowError(err instanceof Error ? err.message : "Send failed"); }
    finally { setBusy(false); onChanged(); }
  }

  async function remove() {
    if (!window.confirm(`Delete invitation for ${guest.name}?`)) return;
    setBusy(true);
    try { await adminDeleteGuest(session, guest.token); onChanged(); }
    finally { setBusy(false); }
  }

  async function toggleRevoke() {
    setBusy(true);
    try { await adminUpdateGuest(session, guest.token, { revoked: !guest.revoked }); onChanged(); }
    finally { setBusy(false); }
  }

  async function reset() {
    if (!window.confirm(`Reset ${guest.name} to Pending? This clears their invitation, viewed status, and RSVP.`)) return;
    setBusy(true);
    try { await adminUpdateGuest(session, guest.token, { reset: true }); onChanged(); }
    finally { setBusy(false); }
  }

  function copy() {
    navigator.clipboard.writeText(`${origin}/i/${guest.token}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <tr className={`border-t border-neutral-100 align-middle ${guest.revoked ? "opacity-50" : ""}`}>
      <td className="px-3 py-3">
        <input type="checkbox" className="accent-accent" checked={checked} onChange={onToggle} />
      </td>
      <td className="px-3 py-3 font-medium">
        <span className="flex items-center gap-2">
          {guest.name}
          {guest.isTest && <span className="inline-block h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" title="Test guest" />}
          {guest.viewedAt && !guest.rsvp && <FiEye size={13} className="text-violet-500 flex-shrink-0" title={`Viewed ${new Date(guest.viewedAt).toLocaleString()}`} />}
        </span>
      </td>
      <td className="px-3 py-3 text-sm text-muted">{guest.email ?? "—"}</td>
      <td className="px-3 py-3 text-sm text-muted">{guest.phone ? <PhoneCell phone={guest.phone} /> : "—"}</td>
      <td className="px-3 py-3 text-sm text-muted">{guest.tableNumber ?? "—"}</td>
      <td className="px-3 py-3 text-center text-sm">{rsvpAdults}</td>
      <td className="px-3 py-3 text-center text-sm">{rsvpKids}</td>
      <td className="px-3 py-3"><StatusBadge guest={guest} /></td>
      <td className="px-3 py-3">
        {(rowError || guest.invitation?.error) && (
          <p className="mb-2 max-w-xs text-xs text-red-700">{rowError ?? guest.invitation?.error}</p>
        )}
        <div className="flex divide-x divide-black/10 overflow-hidden rounded-full border border-black/10 w-fit">
          <button type="button" className="btn-group-item" onClick={() => onEdit(guest)} title="Edit guest">
            <FiEdit2 size={14} />
          </button>
          <button type="button" className="btn-group-item" onClick={copy} title="Copy invite link">
            {copied ? <FiCheck size={14} className="text-green-600" /> : <FiLink size={14} />}
          </button>
          {isSent ? (
            <>
              <button type="button" className="btn-group-item" disabled={busy || !guest.email || guest.revoked} onClick={send} title="Resend invite">
                <FiRotateCcw size={14} /><span>Resend</span>
              </button>
              <button type="button" className="btn-group-item" disabled={busy} onClick={toggleRevoke} title={guest.revoked ? "Restore" : "Revoke"}>
                <FiSlash size={14} />
              </button>
              <button type="button" className="btn-group-item text-amber-600 hover:bg-amber-50" disabled={busy} onClick={reset} title="Reset to Pending">
                <FiRefreshCw size={14} />
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-group-item" disabled={busy || !guest.email} onClick={send} title="Send invite">
                <FiMail size={14} /><span>Send</span>
              </button>
              <button type="button" className="btn-group-item text-red-600 hover:bg-red-50" disabled={busy} onClick={remove} title="Delete guest">
                <FiTrash2 size={14} />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function BulkConfirmModal({
  guests,
  onConfirm,
  onClose,
}: {
  guests: AdminGuestRow[];
  onConfirm: () => void;
  onClose: () => void;
}) {
  const label = "Send / Resend invites";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card w-full max-w-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{label}</h2>
          <button type="button" className="text-muted hover:text-ink transition" onClick={onClose}><FiX size={20} /></button>
        </div>
        <p className="text-sm text-muted mb-3">Invitations will be sent (or resent) to {guests.length} guest{guests.length !== 1 ? "s" : ""}:</p>
        <ul className="mb-5 max-h-48 overflow-y-auto space-y-1 rounded-lg bg-neutral-50 p-3 text-sm">
          {guests.map((g) => (
            <li key={g.token} className="flex items-center gap-2">
              {g.isTest && <span className="inline-block h-2 w-2 rounded-full bg-amber-400 flex-shrink-0" />}
              <span>{g.name}</span>
              <span className="text-muted">({g.email})</span>
            </li>
          ))}
        </ul>
        <div className="flex gap-3">
          <button className="btn-primary" onClick={onConfirm}>{label}</button>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SeatsCard({ data, origin }: { data: AdminListResponse; origin: string }) {
  const seatsUrl = `${origin}/seats/${data.event.eventId}`;
  const svgRef = useRef<HTMLDivElement | null>(null);
  const svgString = useRef<string>("");

  useEffect(() => {
    QRCode.toString(seatsUrl, { type: "svg", margin: 2 })
      .then((svg) => {
        svgString.current = svg;
        if (svgRef.current) svgRef.current.innerHTML = svg;
      })
      .catch(() => undefined);
  }, [seatsUrl]);

  function copyLink() {
    navigator.clipboard.writeText(seatsUrl).catch(() => undefined);
  }

  function downloadPng() {
    const svg = svgString.current;
    if (!svg) return;
    // Ensure the SVG has a proper xmlns so browsers accept it as an image source.
    const withNs = svg.includes("xmlns=")
      ? svg
      : svg.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withNs)}`;
    const SIZE = 512; // 2× the 256px display size for crisp printing
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `seats-qr-${data.event.eventId}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }, "image/png");
    };
    img.src = dataUrl;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">Find Your Seat</h2>
      <p className="text-sm text-muted">Share this QR code or link so guests can look up their table assignment.</p>
      <div className="card mt-3 flex flex-wrap items-start gap-6">
        <div ref={svgRef} className="h-32 w-32 shrink-0 [&_svg]:h-full [&_svg]:w-full" />
        <div className="flex flex-col gap-2 justify-center">
          <p className="text-sm font-medium break-all">{seatsUrl}</p>
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost text-sm flex items-center gap-1.5" onClick={copyLink}>
              <FiCopy size={13} /> Copy link
            </button>
            <a href={seatsUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost text-sm flex items-center gap-1.5">
              <FiLink size={13} /> Open
            </a>
            <button className="btn-ghost text-sm flex items-center gap-1.5" onClick={downloadPng}>
              <FiDownload size={13} /> Download PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SenderSettings({ data, session, onSaved }: { data: AdminListResponse; session: string; onSaved: () => void }) {
  const [email, setEmail] = useState(data.event.invitationSender?.email ?? "");
  const [name, setName] = useState(data.event.invitationSender?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setMessage(null);
    try {
      await adminUpdateEvent(session, data.event.eventId, { senderEmail: email.trim(), senderName: name.trim() || undefined });
      setMessage("Sender saved.");
      onSaved();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not save sender");
    } finally { setBusy(false); }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">Sender</h2>
      <p className="text-sm text-muted">The name and address guests see in their inbox.</p>
      <form className="card mt-3 flex flex-wrap items-end gap-4" onSubmit={save}>
        <label className="min-w-48 flex-1">
          <span className="text-sm text-muted">Sender name</span>
          <input className="field mt-1" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="min-w-56 flex-1">
          <span className="text-sm text-muted">Sender email</span>
          <input required type="email" className="field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <button className="btn-primary" disabled={busy || !email.trim()}>{busy ? "Saving…" : "Save sender"}</button>
        {message && <p className="w-full text-sm text-muted">{message}</p>}
      </form>
    </div>
  );
}

function RsvpSettings({ data, session, onSaved }: { data: AdminListResponse; session: string; onSaved: () => void }) {
  const [closed, setClosed] = useState(data.event.rsvpClosed ?? false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function toggle() {
    const next = !closed;
    setBusy(true); setMessage(null);
    try {
      await adminUpdateEvent(session, data.event.eventId, { rsvpClosed: next });
      setClosed(next);
      setMessage(next ? "RSVP is now closed." : "RSVP is now open.");
      onSaved();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Could not update setting");
    } finally { setBusy(false); }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">RSVP</h2>
      <p className="text-sm text-muted">Control whether guests can submit or change their RSVP.</p>
      <div className="card mt-3 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">{closed ? "RSVP closed" : "RSVP open"}</p>
          <p className="text-sm text-muted">
            {closed
              ? "Guests cannot submit or change their RSVP. They will see a message to contact you."
              : "Guests can submit and update their RSVP normally."}
          </p>
        </div>
        <button
          className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition ${closed ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
          disabled={busy}
          onClick={toggle}
        >
          {busy ? "Saving…" : closed ? "Re-open RSVP" : "Close RSVP"}
        </button>
      </div>
      {message && <p className="mt-2 text-sm text-muted">{message}</p>}
    </div>
  );
}

function EventDetails({ data, session, onSaved }: { data: AdminListResponse; session: string; onSaved: () => void }) {
  const [title, setTitle] = useState(data.event.title);
  const [date, setDate] = useState(data.event.date.slice(0, 16));
  const [location, setLocation] = useState(data.event.location ?? "");
  const [address, setAddress] = useState(data.event.address ?? "");
  const [caps, setCaps] = useState(data.event.rsvpCaps);
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault(); setBusy(true);
    try { await adminUpdateEvent(session, data.event.eventId, { title, date, location, address, rsvpCaps: caps }); onSaved(); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <h2 className="text-lg font-semibold">Event details</h2>
      <p className="text-sm text-muted">Title, date, and venue shown to guests.</p>
      <form className="card mt-3 grid gap-4 sm:grid-cols-2" onSubmit={save}>
        <label>
          <span className="text-sm text-muted">Title</span>
          <input required className="field mt-1" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          <span className="text-sm text-muted">Date and time</span>
          <input required type="datetime-local" className="field mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          <span className="text-sm text-muted">Venue</span>
          <input className="field mt-1" value={location} onChange={(e) => setLocation(e.target.value)} />
        </label>
        <label>
          <span className="text-sm text-muted">Address</span>
          <input className="field mt-1" value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <div>
          <span className="text-sm text-muted">Default adults</span>
          <CapInput value={caps.adults} onChange={(adults) => setCaps({ ...caps, adults })} />
        </div>
        <div>
          <span className="text-sm text-muted">Default kids</span>
          <CapInput value={caps.kids} onChange={(kids) => setCaps({ ...caps, kids })} />
        </div>
        <button className="btn-primary sm:col-span-2 sm:justify-self-start" disabled={busy}>{busy ? "Saving…" : "Save details"}</button>
      </form>
    </div>
  );
}

function EventDirectory({ session, events, onRefresh, onOpen }: { session: string; events: AdminEventSummary[]; onRefresh: () => void; onOpen: (eventId: string) => void }) {
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [eventId, setEventId] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [caps, setCaps] = useState<RsvpCaps>(DEFAULT_NEW_CAPS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = events.filter((e) =>
    `${e.title} ${e.eventId} ${e.location ?? ""}`.toLowerCase().includes(query.toLowerCase())
  );

  function updateTitle(value: string) {
    setTitle(value);
    if (!eventId || eventId === title.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")) {
      setEventId(value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }

  async function create(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      await adminCreateEvent(session, { eventId, title, date, location: location || undefined, address: address || undefined, rsvpCaps: caps });
      onRefresh();
      onOpen(eventId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create event");
    } finally { setBusy(false); }
  }

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-3xl font-semibold">Events</h2>
          <p className="text-muted">Choose an event to manage its invitations.</p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(!creating)}>{creating ? "Cancel" : "+ New event"}</button>
      </div>
      {creating && (
        <form className="card mt-6 grid gap-4 sm:grid-cols-2" onSubmit={create}>
          <label>
            <span className="text-sm text-muted">Event title</span>
            <input required className="field mt-1" value={title} onChange={(e) => updateTitle(e.target.value)} />
          </label>
          <label>
            <span className="text-sm text-muted">Event ID</span>
            <input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" className="field mt-1" value={eventId} onChange={(e) => setEventId(e.target.value.toLowerCase())} />
            <small className="text-muted">Used in admin and URLs.</small>
          </label>
          <label>
            <span className="text-sm text-muted">Date and time</span>
            <input required type="datetime-local" className="field mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            <span className="text-sm text-muted">Venue</span>
            <input className="field mt-1" value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className="sm:col-span-2">
            <span className="text-sm text-muted">Address</span>
            <input className="field mt-1" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <div>
            <span className="text-sm text-muted">Default adults</span>
            <CapInput value={caps.adults} onChange={(adults) => setCaps({ ...caps, adults })} />
          </div>
          <div>
            <span className="text-sm text-muted">Default kids</span>
            <CapInput value={caps.kids} onChange={(kids) => setCaps({ ...caps, kids })} />
          </div>
          {error && <p className="text-red-700 sm:col-span-2">{error}</p>}
          <button className="btn-primary sm:col-span-2 sm:justify-self-start" disabled={busy}>{busy ? "Creating…" : "Create event"}</button>
        </form>
      )}
      <input className="field mt-6" type="search" placeholder="Search events by name, ID, or venue" value={query} onChange={(e) => setQuery(e.target.value)} />
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {filtered.map((event) => (
          <button key={event.eventId} className="card text-left transition hover:-translate-y-0.5 hover:ring-accent/30" onClick={() => onOpen(event.eventId)}>
            <span className="block text-xl font-semibold">{event.title}</span>
            <span className="mt-1 block text-sm text-muted">{event.date ? new Date(event.date).toLocaleString() : "Date not set"}{event.location ? ` · ${event.location}` : ""}</span>
            <span className="mt-3 block text-xs uppercase tracking-wide text-muted">{event.eventId}</span>
          </button>
        ))}
      </div>
      {filtered.length === 0 && <p className="mt-6 text-center text-muted">No matching events.</p>}
    </section>
  );
}

export function AdminPage() {
  const [session, setSession] = useState<string | null>(
    () => sessionStorage.getItem(SESSION_KEY)
  );
  const [password, setPassword] = useState("");
  const [eventId, setEventId] = useState("");
  const [section, setSection] = useState<"overview" | "guests" | "settings">("overview");
  const [guestTab, setGuestTab] = useState<"active" | "test" | "revoked">("active");
  const [modal, setModal] = useState<{ mode: "add" } | { mode: "edit"; guest: AdminGuestRow } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<"send" | "resend" | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [events, setEvents] = useState<AdminEventSummary[]>([]);
  const [data, setData] = useState<AdminListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refreshEvents() {
    if (!session) return;
    try { setEvents((await adminEvents(session)).events); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not load events"); }
  }

  useEffect(() => { void refreshEvents(); }, [session]);

  async function onLogin(e: React.FormEvent) {
    e.preventDefault(); setError(null); setBusy(true);
    try {
      const res = await adminLogin(password);
      sessionStorage.setItem(SESSION_KEY, res.token);
      setSession(res.token);
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally { setBusy(false); }
  }

  async function load(id = eventId) {
    if (!session || !id) return;
    setEventId(id); setError(null); setBusy(true);
    try { setData(await adminList(id, session)); }
    catch (err) {
      if (err instanceof Error && /unauthorized/i.test(err.message)) {
        sessionStorage.removeItem(SESSION_KEY); setSession(null);
      }
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally { setBusy(false); }
  }

  function logout() {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null); setData(null);
  }

  if (!session) {
    return (
      <main className="grid min-h-dvh place-items-center p-6">
        <form className="card max-w-sm" onSubmit={onLogin}>
          <h1 className="text-2xl font-semibold">Admin</h1>
          <label className="mt-4 block">
            <span className="text-sm text-muted">Password</span>
            <input type="password" className="field mt-1" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          </label>
          {error && <p className="mt-3 text-red-700">{error}</p>}
          <button className="btn-primary mt-5 w-full" disabled={busy}>{busy ? "…" : "Sign in"}</button>
        </form>
      </main>
    );
  }

  const origin = window.location.origin;

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {data && (
            <button className="btn-ghost" onClick={() => { setData(null); setEventId(""); setSection("overview"); setModal(null); setSelected(new Set()); void refreshEvents(); }}>
              ← Events
            </button>
          )}
          <h1 className="text-2xl font-semibold">{data ? data.event.title : "Event admin"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {data && data.summary.testCount > 0 && (
            <button
              className="btn-ghost flex items-center gap-1.5 text-sm text-amber-700 border-amber-300 hover:bg-amber-50"
              disabled={bulkBusy}
              onClick={() => {
                const testGuests = data.guests.filter((g) => g.isTest && g.email);
                setSelected(new Set(testGuests.map((g) => g.token)));
                setBulkConfirm("send");
              }}
            >
              <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
              Test run ({data.summary.testCount})
            </button>
          )}
          <button className="btn-ghost" onClick={logout}>Sign out</button>
        </div>
      </header>

      {error && <p className="mt-3 text-red-700">{error}</p>}

      {!data && <EventDirectory session={session} events={events} onRefresh={refreshEvents} onOpen={(id) => void load(id)} />}

      {data && (
        <>
          <nav className="mt-6 flex gap-1 border-b border-accent/10">
            {(["overview", "guests", "settings"] as const).map((item) => (
              <button
                key={item}
                className={`px-4 py-3 capitalize ${section === item ? "border-b-2 border-accent font-semibold text-accent" : "text-muted"}`}
                onClick={() => setSection(item)}
              >
                {item}
              </button>
            ))}
          </nav>

          {/* Overview */}
          {section === "overview" && (
            <section className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="card">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted">Guests</p>
                <p className="mt-1 text-4xl font-semibold">{data.summary.total}</p>
                <p className="mt-2 text-sm text-muted">{data.summary.invitesSent} invite{data.summary.invitesSent !== 1 ? "s" : ""} sent</p>
                {data.summary.viewedNotResponded > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-sm text-violet-600">
                    <FiEye size={13} /> {data.summary.viewedNotResponded} viewed, awaiting response
                  </p>
                )}
              </div>
              <div className="card">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted">Responded</p>
                <p className="mt-1 text-4xl font-semibold">{data.summary.respondedYes + data.summary.respondedNo}</p>
                <p className="mt-2 text-sm text-muted">
                  <span className="text-green-700">{data.summary.respondedYes} yes</span>
                  <span className="mx-1 text-muted">·</span>
                  <span className="text-red-700">{data.summary.respondedNo} no</span>
                </p>
              </div>
              <div className="card">
                <p className="text-sm font-semibold uppercase tracking-wide text-muted">Attending</p>
                <p className="mt-1 text-4xl font-semibold">{data.summary.attendingAdults + data.summary.attendingKids}</p>
                <p className="mt-2 text-sm text-muted">{data.summary.attendingAdults} adults · {data.summary.attendingKids} kids</p>
              </div>
              {data.summary.testCount > 0 && (
                <div className="card border-amber-200 ring-amber-200">
                  <p className="text-sm font-semibold uppercase tracking-wide text-amber-600">Test guests</p>
                  <p className="mt-1 text-4xl font-semibold">{data.summary.testCount}</p>
                  <p className="mt-2 text-sm text-muted">{data.summary.testAttendingAdults} adults · {data.summary.testAttendingKids} kids</p>
                </div>
              )}
            </section>
          )}

          {/* Guests */}
          {section === "guests" && (() => {
            const production = data.guests.filter((g) => !g.isTest && !g.revoked);
            const tests = data.guests.filter((g) => g.isTest);
            const revoked = data.guests.filter((g) => g.revoked);
            const tabGuests = guestTab === "active" ? production : guestTab === "test" ? tests : revoked;
            const allTokens = tabGuests.map((g) => g.token);
            const allSelected = allTokens.length > 0 && allTokens.every((t) => selected.has(t));
            const selectedGuests = tabGuests.filter((g) => selected.has(g.token));
            const pendingGuests = production.filter((g) => !g.invitation?.sentAt && g.email);

            function toggleAll() {
              if (allSelected) setSelected(new Set());
              else setSelected(new Set(allTokens));
            }
            function toggleOne(token: string) {
              setSelected((prev) => {
                const next = new Set(prev);
                next.has(token) ? next.delete(token) : next.add(token);
                return next;
              });
            }

            async function runBulkSend() {
              if (!session) return;
              setBulkBusy(true);
              setBulkConfirm(null);
              for (const g of selectedGuests) {
                try { await adminSendInvite(session, g.token); } catch { /* best effort */ }
              }
              setSelected(new Set());
              setBulkBusy(false);
              void load();
            }

            return (
              <section className="mt-6">
                {/* Sub-tabs */}
                <div className="mb-4 flex gap-1 rounded-full bg-neutral-100 p-1 w-fit">
                  {([
                    { key: "active", label: "Active", count: production.length },
                    { key: "test",   label: "Test",   count: tests.length },
                    { key: "revoked",label: "Revoked",count: revoked.length },
                  ] as const).map(({ key, label, count }) => (
                    <button
                      key={key}
                      onClick={() => { setGuestTab(key); setSelected(new Set()); }}
                      className={`rounded-full px-4 py-1 text-sm transition ${guestTab === key ? "bg-accent text-white" : "text-muted hover:bg-neutral-200"}`}
                    >
                      {label} ({count})
                    </button>
                  ))}
                </div>

                {/* Header row */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-muted">
                    {tabGuests.length} {guestTab === "active" ? "production guest" : guestTab === "test" ? "test guest" : "revoked guest"}{tabGuests.length !== 1 ? "s" : ""}
                  </p>
                  <div className="flex gap-2">
                    {guestTab === "active" && pendingGuests.length > 0 && (
                      <button
                        className="btn-ghost text-sm"
                        onClick={() => { setSelected(new Set(pendingGuests.map((g) => g.token))); setBulkConfirm("send"); }}
                      >
                        Send all pending ({pendingGuests.length})
                      </button>
                    )}
                    {guestTab === "active" && (
                      <button className="btn-primary" onClick={() => setModal({ mode: "add" })}>+ Add guest</button>
                    )}
                  </div>
                </div>

                {/* Bulk action bar */}
                {selected.size > 0 && (
                  <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl bg-accent/10 px-4 py-2.5">
                    <span className="text-sm font-medium">{selected.size} selected</span>
                    <button className="btn-ghost text-sm flex items-center gap-1.5" disabled={bulkBusy} onClick={() => setBulkConfirm("send")}>
                      <FiMail size={13} /> Send / Resend
                    </button>
                    <button className="ml-auto text-sm text-muted hover:text-ink" onClick={() => setSelected(new Set())}>Deselect all</button>
                  </div>
                )}

                <div className="overflow-x-auto rounded-2xl shadow-xl">
                  <table className="w-full min-w-[60rem] border-collapse bg-white text-left">
                    <thead>
                      <tr className="bg-accent/10 text-sm">
                        <th className="px-3 py-3">
                          <input type="checkbox" className="accent-accent" checked={allSelected} onChange={toggleAll} />
                        </th>
                        <th className="px-3 py-3 font-semibold">Guest</th>
                        <th className="px-3 py-3 font-semibold">Email</th>
                        <th className="px-3 py-3 font-semibold">Phone</th>
                        <th className="px-3 py-3 font-semibold">Table</th>
                        <th className="px-3 py-3 text-center font-semibold">Adults</th>
                        <th className="px-3 py-3 text-center font-semibold">Kids</th>
                        <th className="px-3 py-3 font-semibold">Status</th>
                        <th className="px-3 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabGuests.map((g) => (
                        <GuestRow
                          key={g.token}
                          guest={g}
                          session={session}
                          origin={origin}
                          onChanged={() => { setSelected(new Set()); void load(); }}
                          onEdit={(g) => setModal({ mode: "edit", guest: g })}
                          checked={selected.has(g.token)}
                          onToggle={() => toggleOne(g.token)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                {tabGuests.length === 0 && (
                  <p className="mt-4 text-center text-muted">
                    {guestTab === "active" ? "No active guests yet. Add one above." : guestTab === "test" ? "No test guests." : "No revoked guests."}
                  </p>
                )}

                {bulkConfirm && (
                  <BulkConfirmModal
                    guests={selectedGuests}
                    onConfirm={() => void runBulkSend()}
                    onClose={() => setBulkConfirm(null)}
                  />
                )}
              </section>
            );
          })()}

          {/* Settings */}
          {section === "settings" && (
            <section className="mt-6 space-y-10">
              <RsvpSettings data={data} session={session} onSaved={() => void load()} />
              <SeatsCard data={data} origin={origin} />
              <EventDetails data={data} session={session} onSaved={() => void load()} />
              <SenderSettings data={data} session={session} onSaved={() => void load()} />
            </section>
          )}
        </>
      )}

      {/* Add / Edit modal */}
      {modal && data && (
        <GuestModal
          mode={modal.mode}
          guest={modal.mode === "edit" ? modal.guest : undefined}
          eventId={eventId}
          session={session}
          onDone={() => { void load(); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </main>
  );
}
